import { Routes, Route, NavLink, useLocation, Navigate } from 'react-router-dom';
import {
  BarChart3,
  ShoppingCart,
  Package,
  ArrowLeftRight,
  CreditCard,
  Calculator,
  Gift,
  Wallet,
  Users,
  Share2,
  LayoutGrid,
  Bell,
  ClipboardCheck,
  RefreshCw,
  DollarSign,
  Truck,
} from 'lucide-react';
import { DashboardLayout } from '@/components/layout/DashboardLayout';
import { cn } from '@/lib/utils';
import { useAuth } from '@/hooks/useAuth';

// Import sub-pages
import RetailDashboard from './RetailDashboard';
import SalesReports from './SalesReports';
import InventoryReports from './InventoryReports';
import AdjustmentReports from './AdjustmentReports';
import PaymentReports from './PaymentReports';
import RegisterClosures from './RegisterClosures';
import GiftCardReports from './GiftCardReports';
import StoreCreditReports from './StoreCreditReports';
import UserReports from './UserReports';
import SharedReports from './SharedReports';
import CategoryReports from './CategoryReports';
import SLAReports from './SLAReports';
import CashierVarianceReport from './CashierVarianceReport';
import ReorderListReport from './ReorderListReport';
import AssetReport from './AssetReport';
import InTransitReport from './InTransitReport';

interface NavItem {
  id: string;
  label: string;
  icon: React.ElementType;
  path: string;
}

const navItems: NavItem[] = [
  {
    id: 'dashboard',
    label: 'Retail dashboard',
    icon: BarChart3,
    path: '/reports/dashboard',
  },
  {
    id: 'sales',
    label: 'Sales reports',
    icon: ShoppingCart,
    path: '/reports/sales',
  },
  {
    id: 'inventory',
    label: 'Inventory reports',
    icon: Package,
    path: '/reports/inventory',
  },
  {
    id: 'reorder-list',
    label: 'Reorder list',
    icon: RefreshCw,
    path: '/reports/reorder-list',
  },
  {
    id: 'asset-report',
    label: 'Asset report',
    icon: DollarSign,
    path: '/reports/asset-report',
  },
  {
    id: 'in-transit',
    label: 'In-transit',
    icon: Truck,
    path: '/reports/in-transit',
  },
  {
    id: 'adjustments',
    label: 'Adjustment reports',
    icon: ArrowLeftRight,
    path: '/reports/adjustments',
  },
  {
    id: 'payments',
    label: 'Payment reports',
    icon: CreditCard,
    path: '/reports/payments',
  },
  {
    id: 'register-closures',
    label: 'Register closures',
    icon: Calculator,
    path: '/reports/register-closures',
  },
  {
    id: 'gift-cards',
    label: 'Gift card reports',
    icon: Gift,
    path: '/reports/gift-cards',
  },
  {
    id: 'store-credits',
    label: 'Store credit reports',
    icon: Wallet,
    path: '/reports/store-credits',
  },
  {
    id: 'category',
    label: 'Category reports',
    icon: LayoutGrid,
    path: '/reports/category',
  },
  {
    id: 'sla',
    label: 'SLA reports',
    icon: Bell,
    path: '/reports/sla',
  },
  {
    id: 'cashier-variance',
    label: 'Cashier variance',
    icon: ClipboardCheck,
    path: '/reports/cashier-variance',
  },
  {
    id: 'users',
    label: 'User reports',
    icon: Users,
    path: '/reports/users',
  },
  {
    id: 'shared',
    label: 'Shared reports',
    icon: Share2,
    path: '/reports/shared',
  },
];

export default function Reports() {
  useLocation();
  useAuth();

  const header = (
    <div className="flex items-center justify-between gap-4">
      <div className="flex items-center gap-3">
        <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary/10">
          <BarChart3 className="h-5 w-5 text-primary" />
        </div>
        <div>
          <h1 className="text-2xl font-display font-semibold tracking-tight">
            Reports
          </h1>
          <p className="text-sm text-muted-foreground">
            Analytics, sales data, and business insights
          </p>
        </div>
      </div>
    </div>
  );

  return (
    <DashboardLayout header={header} fullWidth>
      <div className="flex gap-6 -mt-2 min-w-0 overflow-hidden">
        {/* Secondary Sidebar Navigation */}
        <aside className="w-56 shrink-0">
          <nav className="sticky top-4 space-y-1">
            {navItems.map((item) => {
              const Icon = item.icon;
              return (
                <NavLink
                  key={item.id}
                  to={item.path}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-3 rounded-lg px-3 py-2 text-sm font-medium transition-colors',
                      'hover:bg-muted/70',
                      isActive
                        ? 'bg-primary/10 text-primary'
                        : 'text-muted-foreground hover:text-foreground'
                    )
                  }
                >
                  <Icon className="h-4 w-4" />
                  {item.label}
                </NavLink>
              );
            })}
          </nav>
        </aside>

        {/* Main Content Area */}
        <div className="flex-1 min-w-0 overflow-x-auto">
          <Routes>
            <Route path="dashboard" element={<RetailDashboard />} />
            <Route path="sales" element={<SalesReports />} />
            <Route path="inventory" element={<InventoryReports />} />
            <Route path="reorder-list" element={<ReorderListReport />} />
            <Route path="asset-report" element={<AssetReport />} />
            <Route path="in-transit" element={<InTransitReport />} />
            <Route path="adjustments" element={<AdjustmentReports />} />
            <Route path="payments" element={<PaymentReports />} />
            <Route path="register-closures" element={<RegisterClosures />} />
            <Route path="gift-cards" element={<GiftCardReports />} />
            <Route path="store-credits" element={<StoreCreditReports />} />
            <Route path="category" element={<CategoryReports />} />
            <Route path="sla" element={<SLAReports />} />
            <Route path="cashier-variance" element={<CashierVarianceReport />} />
            <Route path="users" element={<UserReports />} />
            <Route path="shared" element={<SharedReports />} />
            <Route path="*" element={<Navigate to="/reports/dashboard" replace />} />
          </Routes>
        </div>
      </div>
    </DashboardLayout>
  );
}
